import React, { useEffect, useState } from 'react';
import { updateLscLeague, LscApiError } from '../api';
import {
  LEAGUE_VISIBILITY_OPTIONS,
  normalizeLeagueVisibility,
  PUBLIC_DISCOVERY_NOT_LIVE_COPY,
  type LeagueVisibilityValue,
} from '../leagueVisibility';
import type { LscLeague } from '../types';
import ErrorBanner from './ErrorBanner';

interface LeagueVisibilityPanelProps {
  league: LscLeague;
  leagueArchived?: boolean;
  onUpdated?: (league: LscLeague) => void;
}

const LeagueVisibilityPanel: React.FC<LeagueVisibilityPanelProps> = ({
  league,
  leagueArchived = false,
  onUpdated,
}) => {
  const currentVisibility = normalizeLeagueVisibility(league.visibility);
  const [visibility, setVisibility] = useState<LeagueVisibilityValue>(currentVisibility);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    setVisibility(normalizeLeagueVisibility(league.visibility));
  }, [league.visibility]);

  const unchanged = visibility === currentVisibility;
  const selectedOption = LEAGUE_VISIBILITY_OPTIONS.find((opt) => opt.value === visibility);

  const handleSave = async () => {
    if (!league.id || unchanged) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await updateLscLeague(league.id, { visibility });
      setSuccess('League visibility updated.');
      onUpdated?.(updated);
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not update visibility.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card space-y-4" data-testid="league-visibility-panel">
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">League visibility</h2>
        <p className="text-sm text-secondary-400">
          Controls who can access this league workspace. Membership is managed separately.
        </p>
      </div>

      {leagueArchived && (
        <p className="text-sm text-secondary-500" data-testid="visibility-archived-readonly">
          Visibility cannot be changed while the league is archived. Restore the league to edit
          settings.
        </p>
      )}

      <fieldset className="space-y-3" disabled={leagueArchived}>
        {LEAGUE_VISIBILITY_OPTIONS.map((option) => (
          <label
            key={option.value}
            className={`flex gap-3 rounded-lg border p-3 cursor-pointer ${
              visibility === option.value
                ? 'border-primary-500/60 bg-primary-900/20'
                : 'border-secondary-800'
            }`}
            data-testid={`visibility-option-${option.value}`}
          >
            <input
              type="radio"
              name="league-visibility"
              value={option.value}
              checked={visibility === option.value}
              onChange={() => setVisibility(option.value)}
              className="mt-1"
              data-testid={`visibility-radio-${option.value}`}
            />
            <span className="space-y-1">
              <span className="block text-sm font-medium text-white">{option.label}</span>
              <span className="block text-sm text-secondary-400">{option.description}</span>
            </span>
          </label>
        ))}
      </fieldset>

      {visibility === 'public' && (
        <p className="text-sm text-amber-200/90" data-testid="public-discovery-not-live">
          {PUBLIC_DISCOVERY_NOT_LIVE_COPY}
        </p>
      )}

      {selectedOption && (
        <p className="text-xs text-secondary-500">
          Current: <span className="text-secondary-300">{selectedOption.label}</span>
        </p>
      )}

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {success && (
        <p className="text-sm text-emerald-300" data-testid="visibility-save-success">
          {success}
        </p>
      )}

      {!leagueArchived && (
        <button
          type="button"
          className="btn-primary"
          onClick={() => void handleSave()}
          disabled={saving || unchanged}
          data-testid="visibility-save-button"
        >
          {saving ? 'Saving…' : 'Save visibility'}
        </button>
      )}
    </div>
  );
};

export default LeagueVisibilityPanel;
